import DevDataWorkspace, {
  type DevDataSearchState,
} from '@/features/admin/dev-data/DevDataWorkspace.tsx';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useCallback } from 'react';
import { z } from 'zod';

const PER_PAGE_OPTIONS = [25, 50, 100] as const;

function isPerPageOption(
  value: number,
): value is (typeof PER_PAGE_OPTIONS)[number] {
  return PER_PAGE_OPTIONS.some((option) => option === value);
}

const searchSchema = z.object({
  entityId: z.string().optional(),
  recordId: z.string().optional(),
  actionId: z.string().optional(),
  search: z.string().optional(),
  page: z.coerce.number().int().min(1).optional().catch(undefined),
  perPage: z.coerce
    .number()
    .int()
    .refine(isPerPageOption)
    .optional()
    .catch(undefined),
});

export const Route = createFileRoute('/admin/dev-data')({
  component: RouteComponent,
  validateSearch: searchSchema,
  staticData: {
    authorization: {
      role: 'admin',
    },
  },
});

function RouteComponent() {
  const rawSearch = Route.useSearch();
  const search: DevDataSearchState = {
    ...rawSearch,
    page: rawSearch.page ?? 1,
    perPage: rawSearch.perPage ?? 25,
  };
  const navigate = useNavigate();

  const onSearchChange = useCallback(
    (patch: Partial<DevDataSearchState>, options?: { replace?: boolean }) => {
      const nextSearch: DevDataSearchState = {
        entityId: search.entityId,
        recordId: search.recordId,
        actionId: search.actionId,
        search: search.search,
        page: search.page,
        perPage: search.perPage,
        ...patch,
      };

      void navigate({
        to: '/admin/dev-data',
        replace: options?.replace,
        search: {
          entityId: nextSearch.entityId || undefined,
          recordId: nextSearch.recordId || undefined,
          actionId: nextSearch.actionId || undefined,
          search: nextSearch.search || undefined,
          page: nextSearch.page,
          perPage: nextSearch.perPage,
        },
      });
    },
    [navigate, search],
  );

  return <DevDataWorkspace search={search} onSearchChange={onSearchChange} />;
}
